import { isClosed, localDate } from './journal-metrics';

const validMonth = value => /^\d{4}-(0[1-9]|1[0-2])$/.test(String(value || ''));
const dateIsValid = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return false;
  const date = new Date(`${value}T12:00:00`);
  return Number.isFinite(date.getTime()) && localDate(date) === value;
};
const active = trade => trade && !trade.deleted && !trade.archiveFolderId;
const text = value => typeof value === 'string' && value.trim().length > 0;

export function previousCompletedMonth(now = new Date()) {
  return localDate(new Date(now.getFullYear(), now.getMonth() - 1, 1, 12)).slice(0, 7);
}

export function recapMonthLabel(month) {
  return validMonth(month) ? new Date(`${month}-01T12:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'Choose a month';
}

export function recapMonths(trades = [], now = new Date()) {
  const current = localDate(now).slice(0, 7);
  return [...new Set([previousCompletedMonth(now), ...trades.filter(active).filter(trade => dateIsValid(trade.date)).map(trade => trade.date.slice(0, 7)).filter(month => month <= current)])].sort().reverse();
}

export function recordedReturn(trade) {
  if (trade.result === 'BE') return 0;
  if (trade.returnPct == null || String(trade.returnPct).trim() === '') return null;
  const value = Number(trade.returnPct);
  return Number.isFinite(value) ? value : null;
}

function curveDescription(points, total, knownCount) {
  if (!knownCount) return { title: 'No return curve yet', detail: 'Add recorded returns to your closed trades to see the path.' };
  if (knownCount === 1) return { title: 'One recorded result', detail: 'One trade is a starting point, rather than a trend.' };
  const changes = points.slice(1).map((point, index) => point.value - points[index].value);
  const allPositive = changes.every(value => value >= 0), allNegative = changes.every(value => value <= 0);
  const low = Math.min(...points.map(point => point.value));
  if (changes.every(value => value === 0)) return { title: 'A flat finish', detail: 'Every recorded return was zero this month.' };
  if (allPositive) return { title: 'A steady climb', detail: 'The cumulative recorded return never stepped down.' };
  if (allNegative) return { title: 'A downward path', detail: 'The cumulative recorded return never stepped up.' };
  if (total > 0 && low < 0) return { title: 'A recovery into positive ground', detail: 'The curve moved below its starting point and finished above it.' };
  if (total > 0) return { title: 'An uneven climb', detail: 'Gains and setbacks ended above the starting point.' };
  if (total < 0) return { title: 'An uneven decline', detail: 'Gains and setbacks ended below the starting point.' };
  return { title: 'Back to the starting point', detail: 'The recorded gains and losses balanced out.' };
}

function currencyBasis(closed, { accounts = [], showDollar = false, accountSize = 0, currency = 'USD', journalBalance = null } = {}) {
  if (!showDollar || !closed.length) return null;
  if (journalBalance && Number.isFinite(Number(journalBalance.balance)) && Number(journalBalance.balance) > 0 && Number(journalBalance.balance) <= 1e12 && /^[A-Z]{3}$/.test(journalBalance.currency)) {
    try { new Intl.NumberFormat('en-GB', {style:'currency',currency:journalBalance.currency}).format(0); return {size:Number(journalBalance.balance),currency:journalBalance.currency,accountName:'Simple journal balance'}; } catch { return null; }
  }
  const ids = [...new Set(closed.map(trade => trade.accountId || ''))];
  if (ids.length !== 1) return null;
  if (!ids[0] && accounts.length) return null;
  const account = ids[0] ? accounts.find(item => item.id === ids[0]) : null;
  // Percent returns from distinct or unknown accounts cannot be summed into cash.
  if (ids[0] && !account) return null;
  const size = Number(account?.size ?? accountSize), code = account ? account.currency || 'USD' : currency;
  if (!Number.isFinite(size) || size <= 0 || !/^[A-Z]{3}$/.test(code)) return null;
  try { new Intl.NumberFormat('en-GB', { style: 'currency', currency: code }).format(0); } catch { return null; }
  return { size, currency: code, accountName: account?.name || 'Journal account' };
}

export function monthlyRecapData(trades = [], month, options = {}) {
  const selected = validMonth(month) ? trades.filter(active).filter(trade => dateIsValid(trade.date) && trade.date.slice(0, 7) === month).sort((a, b) => a.date.localeCompare(b.date) || Number(a.createdAt || 0) - Number(b.createdAt || 0)) : [];
  const closed = selected.filter(isClosed), openCount = selected.length - closed.length;
  const wins = closed.filter(trade => trade.result === 'WIN').length, losses = closed.filter(trade => trade.result === 'LOSS').length;
  const known = closed.map(trade => ({ trade, value: recordedReturn(trade) })).filter(item => item.value !== null);
  let total = 0, peak = 0, drawdown = 0;
  const points = [{ value: 0, date: '', instrument: 'Start' }];
  known.forEach(({ trade, value }) => {
    total += value; peak = Math.max(peak, total); drawdown = Math.max(drawdown, peak - total);
    points.push({ value: total, date: trade.date, instrument: trade.instrument || 'Unnamed market', change: value });
  });
  const basis = currencyBasis(closed, options), missingReturns = closed.length - known.length;
  const process = [
    { label: 'Risk recorded', count: closed.filter(trade => Number(trade.riskPct) > 0 && Number.isFinite(Number(trade.riskPct))).length },
    { label: 'Setup recorded', count: closed.filter(trade => text(trade.setup)).length },
    { label: 'Reflection recorded', count: closed.filter(trade => text(trade.keyTakeaway) || text(trade.mistakes)).length },
  ];
  const completed = process.reduce((sum, item) => sum + item.count, 0), possible = closed.length * process.length;
  const ranked = [...known].sort((a, b) => b.value - a.value);
  return { month, label: recapMonthLabel(month), selected, closed, total, points, drawdown, wins, losses, breakEven: closed.length - wins - losses, openCount,
    winRate: wins + losses ? wins / (wins + losses) * 100 : null,
    knownCount: known.length, missingReturns, basis, money: basis && known.length ? total * basis.size / 100 : null,
    best: ranked[0] || null, worst: ranked.at(-1) || null,
    markets: [...new Set(selected.map(trade => trade.instrument).filter(Boolean))],
    process, completed, possible, coverage: completed && possible ? Math.round(completed / possible * 100) : null,
    curve: curveDescription(points, total, known.length),
  };
}

export function recapReturn(value, basis = null) {
  if (value == null) return '—';
  const amount = basis ? value * basis.size / 100 : value;
  const signed = amount < 0 ? '−' : amount > 0 ? '+' : '';
  if (basis) return `${signed}${new Intl.NumberFormat('en-GB', { style: 'currency', currency: basis.currency, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 2 }).format(Math.abs(amount))}`;
  return `${signed}${Math.abs(amount).toFixed(2)}%`;
}
